/**
 * Vivi share — product + cart
 * Mobile: WhatsApp quick (wa.me). Desktop: Copy link.
 * Share: native sheet on mobile; fallback panel = More
 */
(function () {
  if (customElements.get('vivi-share')) return;

  const copiedMsg = () =>
    (window.accessibilityStrings && window.accessibilityStrings.shareSuccess) || 'Link copied';

  class ViviShare extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.trigger = this.querySelector('[data-vivi-share-trigger]');
      this.panel = this.querySelector('[data-vivi-share-panel]');
      this.status = this.querySelector('[data-vivi-share-status]');
      this.waLink = this.querySelector('[data-vivi-share-whatsapp]');
      this.copyBtns = this.querySelectorAll('[data-vivi-share-copy]');
      this.copyLabel = this.querySelector('[data-vivi-share-copy-label]');
      this.moreBtn = this.querySelector('[data-vivi-share-more]');
      this._copyLabelDefault = this.copyLabel ? this.copyLabel.textContent : 'Copy link';

      this.syncWhatsAppHref();

      // Show "More" only when the OS share sheet exists
      if (this.moreBtn) {
        this.moreBtn.hidden = !this.canNativeShare();
      }

      this.trigger?.addEventListener('click', (e) => {
        e.preventDefault();
        this.onShareClick().finally(() => this.clearPressed());
      });

      this.copyBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.copyLink().finally(() => this.clearPressed(btn));
        });
      });

      this.moreBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        this.nativeShare()
          .catch(() => this.openPanel())
          .finally(() => this.clearPressed(this.moreBtn));
      });

      this.waLink?.addEventListener('click', () => {
        window.setTimeout(() => {
          this.closePanel();
          this.clearPressed(this.waLink);
        }, 150);
      });

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.clearPressed();
      });

      window.addEventListener('pageshow', () => this.clearPressed());

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.closePanel();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closePanel();
      });
    }

    clearPressed(el) {
      const target = el || document.activeElement;
      if (target && this.contains(target) && typeof target.blur === 'function') {
        target.blur();
      }
      if (this.trigger && typeof this.trigger.blur === 'function') this.trigger.blur();
      if (this.waLink && typeof this.waLink.blur === 'function') this.waLink.blur();
      this.copyBtns?.forEach((btn) => {
        if (typeof btn.blur === 'function') btn.blur();
      });
    }

    get url() {
      return this.getAttribute('data-url') || window.location.href;
    }

    get title() {
      return this.getAttribute('data-title') || document.title;
    }

    get text() {
      return this.getAttribute('data-text') || this.title;
    }

    sharePayload() {
      return {
        title: this.title,
        text: this.text,
        url: this.url,
      };
    }

    whatsAppHref() {
      const body = `${this.text}\n${this.url}`.trim();
      return `https://wa.me/?text=${encodeURIComponent(body)}`;
    }

    syncWhatsAppHref() {
      if (this.waLink) this.waLink.setAttribute('href', this.whatsAppHref());
    }

    canNativeShare() {
      return typeof navigator.share === 'function';
    }

    async nativeShare() {
      const payload = this.sharePayload();
      if (navigator.canShare && !navigator.canShare(payload)) {
        // Some browsers reject text+url combos; try url-only
        await navigator.share({ url: payload.url, title: payload.title });
        return;
      }
      await navigator.share(payload);
    }

    async onShareClick() {
      this.syncWhatsAppHref();

      // Prefer native sheet on touch / when available — opens WhatsApp app if installed
      if (this.canNativeShare() && (this.isLikelyMobile() || this.dataset.preferNative === 'true')) {
        try {
          await this.nativeShare();
          this.setStatus('');
          return;
        } catch (err) {
          // AbortError = user cancelled — do not open noisy fallback
          if (err && err.name === 'AbortError') return;
        }
      }

      this.togglePanel();
    }

    isLikelyMobile() {
      return window.matchMedia('(max-width: 749px), (hover: none) and (pointer: coarse)').matches;
    }

    togglePanel() {
      if (this.classList.contains('is-open')) this.closePanel();
      else this.openPanel();
    }

    openPanel() {
      this.syncWhatsAppHref();
      this.classList.add('is-open');
      this.trigger?.setAttribute('aria-expanded', 'true');
      this.panel?.removeAttribute('hidden');
    }

    closePanel() {
      this.classList.remove('is-open');
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.panel?.setAttribute('hidden', '');
    }

    async copyLink() {
      const url = this.url;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
        } else {
          const input = document.createElement('input');
          input.value = url;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }
        this.setStatus(copiedMsg());
      } catch (e) {
        this.setStatus('Could not copy — select the link manually');
      }
    }

    setStatus(msg) {
      if (this.status) this.status.textContent = msg || '';
      if (this.copyLabel && msg) {
        this.copyLabel.textContent = msg;
        window.clearTimeout(this._copyLabelTimer);
        this._copyLabelTimer = window.setTimeout(() => {
          if (this.copyLabel) this.copyLabel.textContent = this._copyLabelDefault;
        }, 1800);
      }
    }

    /** Cart Ajax may update attributes — keep WhatsApp in sync */
    static get observedAttributes() {
      return ['data-url', 'data-text', 'data-title'];
    }

    attributeChangedCallback() {
      this.syncWhatsAppHref();
    }
  }

  customElements.define('vivi-share', ViviShare);
})();
